import styled, {css} from 'styled-components';
import {ifProp, prop, switchProp} from 'styled-tools';

export interface InputProps {
    $fullWidth?: boolean;
    $variant?: 'outline' | 'filled' | 'text';
    $size?: 'small' | 'medium' | 'large';
    $borderWidth?: string;
    $borderColor?: string;
    $focusedBorderWidth?: string;
    $focusedBorderColor?: string;
    $backgroundColor?: string;
    $error?: boolean;
    $success?: boolean;
    $hasIcon?: boolean;
    $iconPosition?: 'start' | 'end';
}

export interface WrapperProps extends Pick<InputProps, '$error' | '$success' | '$hasIcon' | '$iconPosition'> {

}

export const Wrapper = styled.div<WrapperProps>`
    .input-title {
        display: flex;
        justify-content: space-between;
        flex-wrap: nowrap;

        span.message {
            display: block;
            margin-bottom: 0.5rem;
            font-size: calc(var(--step-0) * 0.67);
            font-family: var(--font-grotesk);
            font-weight: 500;
            line-height: 100%;
            letter-spacing: 0;
            ${ifProp(
                    '$error',
                    css`
                        color: #dc2626;
                    `,
            )}
            ${ifProp(
                    '$success',
                    css`
                        color: #16a34a;
                    `,
            )}

        }
    }

    .input-container {
        position: relative;

        .icon {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            width: var(--bs-gutter);
            height: var(--bs-gutter);
            ${switchProp('$iconPosition', {
                start: css`
                    left: 0.375rem;
                `,
                end: css`
                    right: 0.375rem;
                `,
            })}
            display: inline-flex;
            align-items: center;
        }
    }
`;
export const LabelStyle = styled.label.attrs((props: any) => ({
    className: `rb-label`,
}))`
    margin-bottom: 0.5rem;
    font-family: var(--font-grotesk);
    font-weight: 500;
    font-size: var(--step--1);
    line-height: 100%;
    letter-spacing: 0%;
    color: var(--color-text-dark);
`;
export const InputStyle = styled.input<InputProps>`
    &:not([type="checkbox"]):not([type="raio"]) {
        height: 100%;
        width: 100%;
        //-webkit-appearance: none; 
        -moz-appearance: none;
        appearance: none;
        ${(props) => {
            return props.$variant === 'outline' ? `` :
                    props.$variant === "text" ?
                            css`
                                font-family: inherit;
                                background: ${props.$backgroundColor || 'transparent'};
                                color: #374151;
                                font-weight: 400;
                                outline-style: solid;
                                outline-width: 0px;
                                transition-property: all;
                                transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
                                transition-duration: 150ms;
                                border-width: ${props.$borderWidth || '0px'};
                                font-size: calc(var(--step-0) * 0.69);
                                padding: ${props.$size === 'large' ? '0.75rem' : '0.625rem 0.75rem'};
                                border-radius: ${props.$size === 'large' ? '0.375rem' : '7px'};
                                border-color: ${props.$borderColor || 'var(--color-border)'};

                                &:focus {
                                    outline-width: 0px;
                                    border-width: ${props.$focusedBorderWidth || '0px'};
                                    border-color: ${props.$focusedBorderColor || 'color-mix(in srgb, var(--color-border) 60%, #111827)'};
                                }

                                &:disabled {
                                    background-color: var(--color-secondary-dark);
                                    border-width: 0px;
                                    cursor: not-allowed;
                                }

                                &:placeholder-shown {
                                    border-width: ${props.$borderWidth || '0px'};
                                }

                                &::placeholder {
                                    opacity: 1;
                                }
                            `
                            : `
        font-family: inherit;
        background: ${props.$backgroundColor || 'transparent'};
        color: var(--color-text);
        font-weight: 400;
        outline-style: solid;
        outline-width: 0px;
        transition-property: all;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 150ms;
        border-width: ${props.$borderWidth || '1px'};
        font-size: calc(var(--step-0) * 0.69);
        padding: ${props.$size === 'large' ? '0.75rem' : '0.625rem 0.75rem'};
        border-radius: ${props.$size === 'large' ? '0.375rem' : '7px'};
        border-color: ${props.$borderColor || 'color-mix(in srgb, var(--color-border) 30%, transparent)'};
    
        &:focus {
            outline-width: 0px;
            border-width: ${props.$focusedBorderWidth || '1px'};
            border-color: ${props.$focusedBorderColor || 'color-mix(in srgb, var(--color-border) 40%, transparent)'};
        }
    
        &:disabled {
            background-color: var(--color-secondary-dark);
            border-width: 0px;
            cursor: not-allowed;
        }
    
        &:placeholder-shown {
            border-width: ${props.$borderWidth || '1px'};
            // border-top-color: #e5e7eb;
        }
        &:placeholder {
            opacity: 0;
        }
        
        `;
        }}
        min-width: 200px;

        ${ifProp(
                '$hasIcon',
                css`
                    ${switchProp('$iconPosition', {
                        start: css`
                            padding-inline-start: 30px;

                            ${({theme}: { theme: any }) => theme.breakpoints.down("lg")} {
                                border-radius: 12px !important;
                                padding-inline-start: 35px !important;
                            }
                        `,
                        end: css`
                            padding-inline-end: 30px;

                            ${({theme}: { theme: any }) => theme.breakpoints.down("lg")} {
                                border-radius: 12px !important;
                                padding-inline-end: 35px !important;
                            }
                        `,
                    })}

                `,
        )}
        ${ifProp(
                '$error',
                css`
                    border-color: #ef4444;
                    color: #dc2626;

                    &:focus {
                        border-color: #ef4444;
                    }

                    &::placeholder {
                        color: rgba(220, 38, 38, 0.51);
                    }
                `,
        )}
        ${ifProp(
                '$success',
                css`
                    border-color: #22c55e;
                    color: #16a34a;

                    &:focus {
                        border-color: #22c55e;
                    }

                    &:placeholder-shown {
                        //border-top-color: #86efac;
                    }

                    &::placeholder {
                        color: rgba(34, 197, 94, 0.49);
                    }
                `,
        )}
        ${({theme}: { theme: any }) => theme.breakpoints.down("md")} {
            padding-top: 15px;
            padding-bottom: 15px;
            font-size: 14px;
        }
    }

    &[type="checkbox"] {
        width: auto;
        height: auto;
        cursor: pointer;
        accent-color: #3900BD;
        margin-right: var(--space-2);
        min-width: fit-content;
    }
`;
export const AmountInputComponentStyle = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: inherit;

    .amt-input-wrapper {
        div.cnt-input {
            width: 100%;
            display: flex;
            justify-content: center;
            border: 1px solid #D0D5DD;
            background: color-mix(in srgb, var(--color-primary) 5%, transparent);
            border-radius: var(--space-3);

            input[type="text"] {
                font-size: var(--step-4);
                font-weight: 700;
                text-align: center;
                width: 100%;
                padding: 40px 0px;
                //caret-color: transparent;
            }

            input[type="text"]:focus {
                border-width: 0;
                outline: none;
                user-select: none;
                -webkit-user-select: none;
                -moz-user-select: none;
                -ms-user-select: none;
            }

            input[type="text"]::placeholder {
                color: #98A2B3;
            }


            input[type="text"].active {
                border: 1px solid #3900BD;
                background: #3900BD29;
                color: #3900BD;
            }

            input[type="text"].error {
                border: 1px solid #3900BD;
                background: #3900BD29;
                color: #3900BD;
            }
        }

        div.input-title {
            display: flex;
            justify-content: space-between;
            flex-wrap: nowrap;
            align-items: end;

            span.message {
                display: block;
                margin-bottom: 0.5rem;
                font-size: calc(var(--step-0) * 0.67);
                font-family: SpaceGrotesk, serif;
                font-weight: 500;
                line-height: 100%;
                letter-spacing: 0;
                text-wrap: initial;
            }
        }
    }

    .grid-amt-btn {
        .grid {
            gap: 10px;

            .col-span-1 {
                overflow: hidden;
                width: 100%;
                margin: auto;
                text-align: center;
                cursor: pointer;

                h4 {
                    font-size: var(--step-0);
                    font-width: 400;
                    color: var(--color-text-dark);
                    margin: auto;
                }
            }
        }
    }
    [data-theme="dark"] & {
        .amt-input-wrapper {
            div.cnt-input {
                border: 1px solid color-mix(in srgb, var(--color-border) 60%, transparent);
                input[type="text"]{
                    color: #FFFFFF;
                }
            }
        }
    }
    ${({theme}: { theme: any }) => theme.breakpoints.down("md")} {
        .amt-input-wrapper div.cnt-input input[type="text"] {
            padding: 20px 0px;
            font-size: var(--step-3);
        }
    }
`